// Telling a newly installed service worker that THIS build can offer the update
// by itself, so that it does not force one on us.
//
// The problem being solved is the other half of utils/serviceWorkerUpdates.js.
// That file made a long-lived session able to *discover* a deploy. It cannot help
// a client whose bundle predates it: sw-custom.js promotes a waiting worker only
// when UpdateAvailableBanner posts SKIP_WAITING, and a build with no banner in it
// never posts anything. Such a client installs every new worker and promotes
// none of them, for as long as the device lives. Observed in production in
// September 2026: accounts months behind, recording got-it clicks (an older
// endpoint) and no reveal-answer clicks at all, so an audit of those students
// read as "no reveal clicks" when the truth was "no reveal data".
//
// The fix cannot run in the old bundle, because the old bundle is what is
// broken and it is never going to change. So the new WORKER decides, and the
// only thing it needs from a client is an answer to one question: can you offer
// this update yourself? A build that answers is left alone with its banner. A
// build that says nothing is, by construction, one that cannot ask -- and the
// worker brings it across itself. See the forced-update section of
// src/sw-custom.js for what it does with the answer.
//
// ## Why the worker asks rather than the client announcing
//
// A client cannot reliably announce to the worker that matters. postMessage on
// navigator.serviceWorker.controller reaches the ACTIVE worker, which is the old
// one; reaching the new one means holding a reference to registration.waiting,
// which only exists once the install has finished. The worker asking removes all
// of that: it asks during its own install, from inside the event that is keeping
// it alive, and a reply comes back to the exact worker object that asked.
//
// It also fixes the timing hazard this repo has already been bitten by once (see
// hasWaitingWorker in utils/serviceWorkerUpdates.js): a worker can finish
// installing before anything in the page is listening. The worker asks
// repeatedly across its grace window, so a page that was still booting when the
// first query went out answers a later one.

export const CAPABILITY_QUERY = "UPDATE_CAPABILITY_QUERY"
export const CAPABILITY_ANSWER = "UPDATE_CAPABILITY"

/**
 * Start answering capability queries. Returns a teardown function.
 *
 * Registered from index.js before anything renders, because the answer has to be
 * available at whatever moment the browser happens to install a new worker, and
 * that can be during the very load that is putting this listener in place.
 *
 * `canOfferUpdate` is a flag rather than the mere fact of a reply so that a
 * future build which does NOT render UpdateAvailableBanner can say so honestly
 * and be rescued, instead of answering and then waiting forever for a prompt
 * that never appears.
 *
 * What the flag claims, precisely, is "this BUILD contains an update prompt", not
 * "a prompt is on screen right now". Those come apart in one ordinary case: the
 * banner is rendered inside the logged-in tree, so a client sitting on the login
 * screen answers yes while showing nothing. That is still the right answer,
 * because the announcement is latched in utils/serviceWorkerUpdates.js, so the
 * banner appears as soon as the tree that holds it mounts. Answering no there
 * would reload a child while they were typing their password.
 *
 * `win` is injected for the tests, the same seam utils/staleBuild.js takes.
 *
 * @param {Window} win
 * @param {boolean} canOfferUpdate
 * @returns {function} teardown
 */
export const answerCapabilityQueries = (win = window, canOfferUpdate = true) => {
	const container = win.navigator && win.navigator.serviceWorker

	if (!container || typeof container.addEventListener !== "function") return () => {}

	const onMessage = event => {
		if (!event || !event.data || event.data.type !== CAPABILITY_QUERY) return

		// The ServiceWorker that sent the query, which is the one that needs the
		// answer. Nothing else can be replied to: the active worker is not the one
		// deciding, and there is no other handle on an installing worker from here.
		const asker = event.source

		if (!asker || typeof asker.postMessage !== "function") return

		try {
			asker.postMessage({ type: CAPABILITY_ANSWER, canOfferUpdate })
		} catch (e) {
			// Staying quiet means the worker treats this client as one that cannot
			// offer the update and reloads it onto the new build. That is the right
			// side to fail on: a reload is recoverable, months on a dead build is not.
		}
	}

	container.addEventListener("message", onMessage)

	return () => container.removeEventListener("message", onMessage)
}

export default answerCapabilityQueries
